import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Download, Play, Plus } from 'lucide-react';
import { Mail, Store } from 'lucide-react';
import { WindowBreakpoints } from '@/hooks/useWindowResponsive';
import { cn } from '@/lib/utils';
import { ElegantProgressBar } from '@/components/ui/elegant-progress-bar';
import { motion, AnimatePresence } from 'framer-motion';

interface App {
  id: string;
  name: string;
  description?: string;
  category: string;
  icon_name: string;
  component_type: string;
}

interface AppStoreHeroProps {
  apps: App[];
  onAppAction: (action: string, appId: string, appName: string) => void;
  appStore: any;
  responsive?: WindowBreakpoints & {
    isNarrow: boolean;
    isMedium: boolean;
  };
  installationProgress?: Map<string, number>;
}

// Helper function to get the correct icon component
const getIconComponent = (iconName: string) => {
  switch (iconName) {
    case 'Mail':
      return Mail;
    case 'Store':
      return Store;
    default:
      return Store;
  }
};

export function AppStoreHero({ apps, onAppAction, appStore, responsive, installationProgress }: AppStoreHeroProps) {
  if (!apps.length) return null;
  
  const featuredApp = apps[0];
  const IconComponent = getIconComponent(featuredApp.icon_name);
  const isInstalled = appStore.isAppInstalled(featuredApp.id);
  const isInDock = appStore.isAppInDock(featuredApp.id);
  const isSystemApp = featuredApp.component_type === 'AppStoreWindow';
  const currentProgress = installationProgress?.get(featuredApp.id);

  return (
    <div className={cn(
      "relative overflow-hidden rounded-xl backdrop-blur-sm",
      "bg-gradient-to-br from-zinc-900 via-zinc-800/90 to-black/95",
      "border border-zinc-700/50",
      "shadow-2xl shadow-black/40",
      responsive?.isNarrow && "rounded-lg"
    )}>
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-gradient-to-br from-transparent via-amber-950/5 to-amber-900/10" />
      <div className={cn(
        "absolute top-0 right-0 bg-gradient-radial from-amber-900/10 to-transparent",
        responsive?.isNarrow ? "w-32 h-32" : "w-64 h-64"
      )} />
      
      <div className={cn(
        "relative",
        responsive?.isNarrow ? "p-4" : responsive?.isMedium ? "p-5" : "p-6"
      )}>
        <div className={cn(
          "flex items-start",
          responsive?.isNarrow ? "flex-col gap-4" : "justify-between"
        )}>
          <div className={cn(
            "flex-1",
            !responsive?.isNarrow && "max-w-2xl"
          )}>
            <div className={cn(
              "flex items-center mb-4 lg:mb-5",
              responsive?.isNarrow ? "flex-col text-center gap-3" : "gap-4"
            )}>
              <div className={cn(
                "rounded-xl backdrop-blur-sm flex items-center justify-center shadow-xl",
                "bg-gradient-to-br from-zinc-800/70 to-zinc-900/90",
                "border border-zinc-700/50",
                responsive?.isNarrow ? "w-14 h-14" : "w-16 h-16"
              )}>
                <IconComponent className={cn(
                  "text-amber-200/90",
                  responsive?.isNarrow ? "h-7 w-7" : "h-8 w-8"
                )} />
              </div>
              <div className={responsive?.isNarrow ? "text-center" : ""}>
                <Badge className="bg-amber-900/30 text-amber-200/90 border-amber-900/50 mb-2 text-xs px-2.5 py-0.5 font-light">
                  Featured
                </Badge>
                <h2 className={cn(
                  "font-light text-zinc-100 leading-tight tracking-wide",
                  responsive?.isNarrow ? "text-xl" : responsive?.isMedium ? "text-2xl" : "text-2xl"
                )}>
                  {featuredApp.name}
                </h2>
                <p className={cn(
                  "text-zinc-400 font-light",
                  responsive?.isNarrow ? "text-sm" : "text-base"
                )}>
                  {featuredApp.category.charAt(0).toUpperCase() + featuredApp.category.slice(1)}
                </p>
              </div>
            </div>
            
            <p className={cn(
              "text-zinc-400 leading-relaxed mb-5 lg:mb-6 font-light",
              responsive?.isNarrow ? "text-sm text-center" : "text-base"
            )}>
              {featuredApp.description || "Discover the power of this elegant application designed to enhance your productivity and streamline your workflow with refined efficiency."}
            </p>
            
            <div className={cn(
              "flex items-center gap-3",
              responsive?.isNarrow && "justify-center flex-wrap"
            )}>
              <AnimatePresence mode="wait">
                {currentProgress !== undefined ? (
                  // Installing: Show Progress
                  <motion.div
                    key="progress"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                    className={responsive?.isNarrow ? "w-full" : "min-w-[200px]"}
                  >
                    <ElegantProgressBar progress={currentProgress} />
                  </motion.div>
                ) : !isInstalled ? (
                  // Not Installed: Show Get Button
                  <motion.div
                    key="get"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Button
                      size="lg"
                      onClick={() => onAppAction('install', featuredApp.id, featuredApp.name)}
                      className={cn(
                        "font-light rounded-lg transition-all duration-300 shadow-xl hover:shadow-2xl",
                        "bg-gradient-to-r from-amber-900/40 via-amber-800/30 to-amber-900/40",
                        "text-amber-100 border border-amber-900/60",
                        "hover:from-amber-800/50 hover:via-amber-700/40 hover:to-amber-800/50",
                        "hover:border-amber-700/70 hover:text-amber-50",
                        "hover:shadow-amber-900/40 hover:scale-[1.02]",
                        "active:scale-[0.98]",
                        responsive?.isNarrow ? "px-5 py-2.5" : "px-8 py-2.5"
                      )}
                    >
                      <Download className="mr-2 h-4 w-4" />
                      Get
                    </Button>
                  </motion.div>
                ) : (
                  // Installed: Show Open Button
                  <motion.div
                    key="open"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                    className="flex items-center gap-3"
                  >
                    <Button
                      size="lg"
                      onClick={() => onAppAction('open', featuredApp.id, featuredApp.name)}
                      className={cn(
                        "font-light rounded-lg transition-all duration-300 shadow-xl hover:shadow-2xl",
                        "bg-gradient-to-r from-zinc-800 via-zinc-700 to-zinc-800",
                        "text-amber-100 border border-zinc-600/60",
                        "hover:from-zinc-700 hover:via-zinc-600 hover:to-zinc-700",
                        "hover:border-amber-900/60 hover:text-amber-50",
                        "hover:shadow-amber-900/30 hover:scale-[1.02]",
                        "active:scale-[0.98]",
                        responsive?.isNarrow ? "px-5 py-2.5" : "px-8 py-2.5"
                      )}
                    >
                      <Play className="mr-2 h-4 w-4" />
                      Open
                    </Button>
                    {!isInDock && (
                      <Button
                        size="lg"
                        variant="outline"
                        onClick={() => onAppAction('addToDock', featuredApp.id, featuredApp.name)}
                        className={cn(
                          "rounded-lg border-zinc-700/50 text-zinc-400 bg-zinc-900/30",
                          "hover:bg-zinc-800/50 hover:border-amber-900/50 hover:text-amber-200",
                          "transition-all duration-200",
                          responsive?.isNarrow ? "px-4 py-2.5" : "px-5 py-2.5"
                        )}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
              
              {isInstalled && currentProgress === undefined && (
                <div className="flex items-center gap-2 text-zinc-400">
                  <div className="w-2 h-2 rounded-full bg-amber-400/80" />
                  <span className={cn(
                    "font-light",
                    responsive?.isNarrow ? "text-xs" : "text-sm"
                  )}>
                    {isInDock ? "In Dock" : "Installed"}
                  </span>
                </div>
              )}
            </div>
          </div>
          
          {!responsive?.isNarrow && !responsive?.isMedium && (
            <div className="hidden lg:block">
              <div className="w-32 h-32 rounded-xl bg-gradient-to-br from-accent/15 to-accent/25 border border-accent/25 flex items-center justify-center">
                <IconComponent className="h-16 w-16 text-accent opacity-40" />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}